/**
 * The dates an override form works in, and what "in force" means to the list.
 *
 * A person picks a **day** — "do końca przyszłego tygodnia" — while the API
 * takes an instant and refuses one past the policy's maximum life. Everything
 * that turns one into the other lives here, pure, so the list and the form
 * cannot disagree about which day is the last one on offer.
 */

const DAY_MS = 24 * 60 * 60 * 1000

/** `YYYY-MM-DD` of an instant, in the browser's own zone — what a date input reads and writes. */
export function localDate(at: Date): string {
  const y = at.getFullYear()
  const m = String(at.getMonth() + 1).padStart(2, "0")
  const d = String(at.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

/**
 * The last instant of a chosen day, in the browser's zone. An override valid
 * "to 21 September" is meant to cover the 21st, not to lapse at midnight
 * before it starts.
 */
export function endOfLocalDay(date: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (match === null) return null
  const at = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 23, 59, 59, 999)
  return Number.isNaN(at.getTime()) ? null : at
}

/** Default short: a week from today, unless the policy allows less. */
export function defaultExpiry(now: Date, maxDurationMs: number): string {
  return localDate(new Date(now.getTime() + Math.min(7 * DAY_MS, maxDurationMs - DAY_MS)))
}

/**
 * The latest day whose end still sits inside the policy's maximum. One day
 * short of the arithmetic on purpose: the end of the day `now + max` lands on
 * is already past the limit unless it is midnight, and a form that offers a
 * date the route then refuses is a form nobody trusts.
 */
export function latestExpiry(now: Date, maxDurationMs: number): string {
  return localDate(new Date(now.getTime() + maxDurationMs - DAY_MS))
}

/**
 * In force right now: not revoked, and not yet expired — or open-ended, which
 * never expires. The list used to split on revocation alone, so an override
 * that had quietly lapsed sat under *Aktywne* for ever — the opposite of what
 * a standing list is for.
 */
export function inForce(
  override: { readonly validUntil: string | null; readonly revokedAt: string | null },
  now: Date,
): boolean {
  if (override.revokedAt !== null) return false
  return override.validUntil === null || new Date(override.validUntil).getTime() > now.getTime()
}
